import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import * as moment from 'moment';
import { AppComponent } from 'src/app/app.component';
import { Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { IoService } from 'src/app/services/io/io.service';
import { LocationService } from 'src/app/services/location/location.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { TagPrintService } from '../../../services/tag-print/tag-print.service';
import { ReportService } from 'src/app/services/report/report.service';
import { ClientSettingsService } from 'src/app/services/client-settings/client-settings.service';

/**
 * Sale Campaign wizard.
 *
 * Campaign types are TEMPLATES: each declares the ordered sections it needs
 * plus the optional restriction sections it supports. Sections are reusable
 * blocks (rendered by key in the template HTML) so future restrictions are
 * added by defining one section and listing it on the templates that support
 * it. Navigation is a sidebar: core sections always visible, restrictions
 * addable via "Add Restriction", any section clickable.
 */
@Component({
  selector: 'app-discount-wizard',
  templateUrl: './discount-wizard.component.html',
  styleUrls: ['./discount-wizard.component.scss']
})
export class DiscountWizardComponent implements OnInit {

  documentId$: Observable<any>;
  documentId: string;

  campaign: any;
  printTemplateAvailable = true;

  // Read-only mode (group.campaign.view without manage): the server marks the
  // campaign readOnly, says whether it is active now, and strips the deal
  // (type, amounts, sale prices) until it is — dealHidden. Nothing is saved
  // from this mode; tags can be printed only while the campaign is active.
  readOnly = false;
  dealHidden = false;
  activeNow = false;
  printTemplates: any[] = [];
  printTemplateSelect = false;
  printingTemplateKey: string = null;

  startDate: Date;
  endDate: Date;

  // ==== Section registry ============================================
  // kind 'core' sections are placed by templates; 'restriction' sections are
  // optional add-ons (unless a template lists them as required).
  sections: any = {
    basics: { key: 'basics', name: 'Basics', kind: 'core' },
    locations: { key: 'locations', name: 'Locations', kind: 'core' },
    items: { key: 'items', name: 'Items', kind: 'core' },
    categoryScope: {
      key: 'categoryScope', name: 'Categories', kind: 'restriction',
      description: 'Restrict by category / subcategory',
    },
    inventoryGroup: {
      key: 'inventoryGroup', name: 'Inventory Groups', kind: 'restriction',
      description: 'Restrict by inventory group',
    },
    excludedInventoryGroup: {
      key: 'excludedInventoryGroup', name: 'Excluded Groups', kind: 'restriction',
      description: 'Omit items in selected inventory groups',
    },
    unitDescription: {
      key: 'unitDescription', name: 'Container Sizes', kind: 'restriction',
      description: 'Restrict by container size',
    },
    userGroup: {
      key: 'userGroup', name: 'User Groups', kind: 'restriction',
      description: 'Only purchases rung up by selected user groups',
    },
    customerGroup: {
      key: 'customerGroup', name: 'Customer Groups', kind: 'restriction',
      description: 'Require an attached customer from selected groups (user groups count, e.g. employees)',
    },
    customerUseLimit: {
      key: 'customerUseLimit', name: 'Per-Customer Limit', kind: 'restriction',
      description: 'Limit how many times each customer can use this discount',
    },
    minimumBuy: {
      key: 'minimumBuy', name: 'Minimum Buy', kind: 'restriction',
      description: 'Require a minimum quantity to qualify',
    },
    minimumPurchase: {
      key: 'minimumPurchase', name: 'Minimum Purchase', kind: 'restriction',
      description: 'Require a minimum qualifying subtotal',
    },
    amount: { key: 'amount', name: 'Amount', kind: 'core' },
    stacking: { key: 'stacking', name: 'Stacking', kind: 'core' },
    application: { key: 'application', name: 'Application', kind: 'core' },
    schedule: { key: 'schedule', name: 'Schedule', kind: 'core' },
    review: { key: 'review', name: 'Review', kind: 'core' },
  };

  // ==== Campaign type templates =====================================
  templates: any = {
    salePrice: {
      name: 'Sale Price',
      description: 'Set a fixed sale price per item',
      sections: ['basics', 'locations', 'items', 'amount', 'stacking', 'application', 'schedule'],
      optional: ['inventoryGroup', 'excludedInventoryGroup', 'unitDescription', 'userGroup', 'customerGroup', 'customerUseLimit'],
    },
    buyOneGetOne: {
      name: 'Buy One Get One',
      description: 'Buy one item, get one free',
      sections: ['basics', 'locations', 'items', 'amount', 'stacking', 'application', 'schedule'],
      optional: ['inventoryGroup', 'excludedInventoryGroup', 'unitDescription', 'userGroup', 'customerGroup', 'customerUseLimit'],
    },
    buyOneGetOneHalfOff: {
      name: 'Buy One Get One 1/2 Off',
      description: 'Buy one item, get one half off',
      sections: ['basics', 'locations', 'items', 'amount', 'stacking', 'application', 'schedule'],
      optional: ['inventoryGroup', 'excludedInventoryGroup', 'unitDescription', 'userGroup', 'customerGroup', 'customerUseLimit'],
    },
    discountedMultiples: {
      name: 'Discounted Multiples',
      description: 'Discount items when purchased in multiples',
      sections: ['basics', 'locations', 'items', 'amount', 'stacking', 'application', 'schedule'],
      optional: ['minimumBuy', 'inventoryGroup', 'excludedInventoryGroup', 'unitDescription', 'userGroup', 'customerGroup', 'customerUseLimit'],
    },
    categoryPercent: {
      name: '% Off',
      description: 'Percent off qualifying items — storewide unless restricted by category, size, group, and more',
      sections: ['basics', 'locations', 'amount', 'stacking', 'application', 'schedule'],
      optional: ['minimumBuy', 'categoryScope', 'unitDescription', 'inventoryGroup', 'excludedInventoryGroup', 'userGroup', 'customerGroup', 'customerUseLimit'],
    },
    amountOff: {
      name: '$ Off',
      description: 'Fixed dollar amount off the purchase, added by the cashier — storewide unless restricted by category, size, group, and more',
      sections: ['basics', 'locations', 'amount', 'stacking', 'application', 'schedule'],
      optional: ['minimumPurchase', 'categoryScope', 'unitDescription', 'inventoryGroup', 'excludedInventoryGroup', 'userGroup', 'customerGroup', 'customerUseLimit'],
    },
  };

  // ==== Amount-table column sets (per template) =====================
  salePriceColumns = [
    { name: 'Item', key: 'name', class: 'item-name', editable: false, pipe: 'string' },
    { name: 'Unit', key: 'unitDescription', class: 'item-unit', editable: false, pipe: 'string' },
    { name: 'List Price', key: 'price', class: 'item-price', editable: false, pipe: 'currency' },
    { name: 'Sale Price', key: 'salePrice', path: 'discountSettings', class: 'item-sale-price', editable: true, pipe: 'currency' },
    { name: 'Discount $', key: 'discountAmount', path: 'discountSettings', class: 'item-discount-amount', editable: true, pipe: 'currency' },
    { name: 'Discount %', key: 'discountPercent', path: 'discountSettings', class: 'item-discount-percent', editable: true, pipe: 'percentage' },
    { name: 'Cost', key: 'cost', class: 'item-cost', editable: false, pipe: 'currency' },
    { name: 'Margin $', key: 'margin', path: 'discountSettings', class: 'item-margin', editable: true, pipe: 'currency' },
    { name: 'Margin %', key: 'marginPercent', path: 'discountSettings', class: 'item-margin-percent', editable: true, pipe: 'percentage' },
  ];

  discountedMultiplesColumns = [
    { name: 'Item', key: 'name', class: 'item-name', editable: false, pipe: 'string' },
    { name: 'Unit', key: 'unitDescription', class: 'item-unit', editable: false, pipe: 'string' },
    { name: 'List Price', key: 'price', class: 'item-price', editable: false, pipe: 'currency' },
    { name: 'Minimum Buy', key: 'threshold', class: 'item-threshold', editable: true, pipe: 'number' },
    { name: 'Discounted Total Price', key: 'discountedTotalPrice', class: 'item-discounted-total-price', editable: true, pipe: 'currency' },
    { name: 'Discounted Unit Price', key: 'discountedUnitPrice', class: 'item-discounted-unit-price', editable: true, pipe: 'currency' },
    { name: 'Discount $', key: 'discountAmount', path: 'discountSettings', class: 'item-discount-amount', editable: false, pipe: 'currency' },
    { name: 'Discount %', key: 'discountPercent', path: 'discountSettings', class: 'item-discount-percent', editable: false, pipe: 'percentage' },
    { name: 'Margin $', key: 'margin', path: 'discountSettings', class: 'item-margin', editable: false, pipe: 'currency' },
    { name: 'Margin %', key: 'marginPercent', path: 'discountSettings', class: 'item-margin-percent', editable: false, pipe: 'percentage' },
  ];

  discountedMultiplesAnyItemColumns = [
    { name: 'Item', key: 'name', class: 'item-name', editable: false, pipe: 'string' },
    { name: 'Unit', key: 'unitDescription', class: 'item-unit', editable: false, pipe: 'string' },
    { name: 'List Price', key: 'price', class: 'item-price', editable: false, pipe: 'currency' },
    { name: 'Discount $', key: 'discountAmount', path: 'discountSettings', class: 'item-discount-amount', editable: false, pipe: 'currency' },
    { name: 'Discount %', key: 'discountPercent', path: 'discountSettings', class: 'item-discount-percent', editable: false, pipe: 'percentage' },
    { name: 'Cost', key: 'cost', class: 'item-cost', editable: false, pipe: 'currency' },
    { name: 'Margin $', key: 'margin', path: 'discountSettings', class: 'item-margin', editable: false, pipe: 'currency' },
    { name: 'Margin %', key: 'marginPercent', path: 'discountSettings', class: 'item-margin-percent', editable: false, pipe: 'percentage' },
  ];

  currencyFields = [
    'discountAmount',
    'salePrice',
    'discountedTotalPrice',
    'discountedUnitPrice',
    'margin'
  ];

  percentFields = [
    'discountPercent',
    'marginPercent'
  ];

  searchText: any;
  searchTimeout: any;
  results: any = [];
  resultIds: any = [];
  selected: any = [];
  categories: any;
  selectedCategory: any = '';
  groups: any;
  selectedGroup: any = '';
  unitDescription: any;
  selectedUnitDescription: any = 'Any';
  // Client-configured Retail Product fields ({ key, name }) shown as extra
  // Find Items filters and on result/selected rows (settings.saleCampaignItemFields).
  itemFields: any = [];
  itemFieldOptions: any = {};
  selectedItemFields: any = {};
  indexedItems: any = {};
  locations: any = null;
  scopeCategories: any = null;
  activeScopeCategory: any = null;
  unitDescriptionOptions: any = [];
  unitSearchText = '';
  inventoryGroupOptions: any = null;
  userGroupOptions: any = null;
  userGroupError = '';
  customerGroupOptions: any = null;
  customerGroupError = '';
  addRestrictionOpen = false;
  // Stable moment instances for the inline range picker (fresh objects each
  // change-detection pass would loop its setters).
  scheduleStart: any = null;
  scheduleEnd: any = null;
  bulkColumnKey = '';
  bulkValue: any = null;
  scopeItemCount: any = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private ioService: IoService,
    private locationService: LocationService,
    private headerService: HeaderService,
    private tagPrintService: TagPrintService,
    private reportService: ReportService,
    private clientSettingsService: ClientSettingsService,
  ) { }

  ngOnInit(): void {
    this.loadItemFields();
    this.documentId$ = this.route.paramMap.pipe(switchMap(params => of(params.get('documentId'))));
    this.documentId$.subscribe(documentId => {
      if (documentId) {
        this.documentId = documentId;
        this.getCampaign();
      }
    });
    this.checkPrintTemplate();
  }

  loadItemFields = async () => {
    await this.clientSettingsService.loadClientSettings();
    this.itemFields = this.clientSettingsService.getSaleCampaignItemFields();
    for (const field of this.itemFields) {
      if (!(field.key in this.selectedItemFields)) {
        this.selectedItemFields[field.key] = 'Any';
      }
      if (!this.itemFieldOptions[field.key]) {
        this.itemFieldOptions[field.key] = ['Any'];
      }
    }
  }

  // Load the print templates configured on the sale-campaign-tags report
  // (its templateKeys array). Hide Print Tags when none exist. Fails open:
  // if the check itself errors, keep the button and let printTags() surface
  // the failure.
  checkPrintTemplate = async () => {
    try {
      const printTemplates: any = await this.ioService.post('/template/getPrintTemplates', {
        reportKey: 'sale-campaign-tags',
      });
      this.printTemplates = Array.isArray(printTemplates) ? printTemplates : [];
      this.printTemplateAvailable = this.printTemplates.length > 0;
    } catch (e) {
      this.printTemplates = [{ name: 'Sale Tags', key: 'sale-tags' }];
      this.printTemplateAvailable = true;
    }
  }

  arrayToObject = (array, keyField) => array.reduce((obj, item) => {
      obj[item[keyField]] = item;
      return obj;
  }, {});

  // ==== Load / save =================================================

  getCampaign = async () => {
    this.campaign = null;

    this.locations = await this.locationService.getLocationsByType('retail');

    let campaign: any = await this.ioService.post('/group/getCampaign', {
      groupId: this.documentId,
      returnInventoryAccounts: true,
    });

    if (campaign.timeRange) {
      this.startDate = new Date(campaign.timeRange.startDate);
      this.endDate = new Date(campaign.timeRange.endDate);
    } else {
      this.startDate = new Date();
      this.endDate = new Date();
      this.endDate.setDate(this.endDate.getDate() + 31);
    }

    campaign.timeRange = {
      startDate: this.startDate,
      endDate: this.endDate,
    };

    if (campaign.discountedUnitPrice) {
      campaign.discountedUnitPrice = campaign.discountedUnitPrice / 100;
    }

    if (campaign.discountedTotalPrice) {
      campaign.discountedTotalPrice = campaign.discountedTotalPrice / 100;
    }

    // Stored as a decimal (0.1); shown as a human percentage (10).
    if (campaign.discountPercent) {
      campaign.discountPercent = campaign.discountPercent * 100;
    }

    // $ Off: stored in cents, edited in dollars.
    for (const centsField of ['amountOff', 'minimumPurchase']) {
      if (campaign[centsField]) {
        campaign[centsField] = campaign[centsField] / 100;
      }
    }

    for (const listField of ['locationId', 'scope', 'scopeUnitDescription', 'addedSections',
                             'scopeInventoryGroups', 'excludedInventoryGroups', 'userGroups', 'customerGroups']) {
      if (!Array.isArray(campaign[listField])) {
        campaign[listField] = [];
      }
    }

    // Not stackable unless explicitly opted in.
    campaign.stackable = campaign.stackable === true;

    // Automatic application unless explicitly set to manual.
    campaign.applyMode = campaign.applyMode || 'auto';
    campaign.requireNote = campaign.requireNote === true;

    // Scheduling is optional per end; campaigns predating the flags had both.
    campaign.scheduleStartEnabled = campaign.scheduleStartEnabled !== false;
    campaign.scheduleEndEnabled = campaign.scheduleEndEnabled !== false;

    // Campaigns saved when scope was a single selection.
    if (!campaign.scope.length && campaign.scopeId) {
      campaign.scope.push({
        type: campaign.scopeType,
        _id: campaign.scopeId,
        name: campaign.scopeName,
      });
    }

    // Every selected inventory account needs `discountSettings` so the amount
    // table can bind `item.discountSettings[field.key]` without throwing.
    if (Array.isArray(campaign.inventoryAccounts)) {
      for (let item of campaign.inventoryAccounts) {
        if (!item.discountSettings) {
          item.discountSettings = {};
        }

        const savedSettings = campaign.inventoryAccountSettings?.[item._id];
        if (!savedSettings) continue;

        for (let settingKey in savedSettings) {
          if (savedSettings[settingKey]) {

            let multiplier = 1;
            if (this.percentFields.indexOf(settingKey) > -1) {
              multiplier = 100;
            }

            if (this.currencyFields.indexOf(settingKey) > -1) {
              multiplier = 0.01;
            }

            item.discountSettings[settingKey] = savedSettings[settingKey] * multiplier;

            if (multiplier != 1) {
              item.discountSettings[settingKey] = item.discountSettings[settingKey].toFixed(2);
            }

          }
        }
      }
    }

    this.readOnly = campaign.readOnly === true || campaign.access === 'view';
    this.dealHidden = campaign.dealHidden === true;
    this.activeNow = campaign.activeNow === true;

    this.campaign = campaign;

    if (this.readOnly) {
      // Viewers land on the review summary and stay there.
      this.campaign.activeStep = 'review';
      await this.loadSectionData('review');
      return;
    }

    // Restore the last section; older campaigns may reference retired steps.
    const startSection = (campaign.activeStep && this.visibleSections().indexOf(campaign.activeStep) !== -1)
      ? campaign.activeStep
      : 'basics';
    this.campaign.activeStep = startSection;
    await this.loadSectionData(startSection);
  };

  // ==== Read-only helpers ===========================================

  // Sidebar type label: the type is part of the hidden deal.
  campaignTypeLabel = (): string => {
    if (this.dealHidden) { return 'Sale Campaign'; }
    return this.template().name || 'Sale Campaign';
  }

  // Where the campaign stands for a viewer: active, scheduled, ended, or off.
  statusLabel = (): string => {
    const campaign = this.campaign;
    if (!campaign) { return ''; }
    if (this.activeNow) { return 'Active'; }
    if (!campaign.live) { return 'Not active'; }
    const now = new Date();
    const start = campaign.scheduleStartEnabled && campaign.timeRange?.startDate ? new Date(campaign.timeRange.startDate) : null;
    const end = campaign.scheduleEndEnabled && campaign.timeRange?.endDate ? new Date(campaign.timeRange.endDate) : null;
    if (start && start > now) { return 'Scheduled — starts ' + start.toLocaleDateString(); }
    if (end && end < now) { return 'Ended ' + end.toLocaleDateString(); }
    return 'Not active';
  }

  dealHiddenNotice = (): string => {
    const campaign = this.campaign;
    const start = campaign?.scheduleStartEnabled && campaign?.timeRange?.startDate ? new Date(campaign.timeRange.startDate) : null;
    if (campaign?.live && start && start > new Date()) {
      return 'Sale prices are shown once the campaign starts on ' + start.toLocaleDateString() + '.';
    }
    return 'Sale prices are shown once the campaign is active.';
  }

  // Tag actions: always for managers; for viewers only while active.
  canPrintTags = (): boolean => {
    return this.printTemplateAvailable && (!this.readOnly || this.activeNow);
  }

  canCreateTagList = (): boolean => {
    return this.campaign?.subtype !== 'amountOff' && !this.dealHidden && (!this.readOnly || this.activeNow);
  }

  saveUpdates = async () => {
    if (this.readOnly) {
      return;
    }
    let inventoryAccountSettings = {};
    if (this.campaign.inventoryAccounts) {
      for (let inventoryAccount of this.campaign.inventoryAccounts) {
        inventoryAccountSettings[inventoryAccount._id] = {};

        for (let settingKey in inventoryAccount.discountSettings) {
          if (inventoryAccount.discountSettings[settingKey]) {

            let multiplier = 1;
            if (this.percentFields.indexOf(settingKey) > -1) {
              multiplier = 0.01;
            }

            if (this.currencyFields.indexOf(settingKey) > -1) {
              multiplier = 100;
            }

            inventoryAccountSettings[inventoryAccount._id][settingKey] = inventoryAccount.discountSettings[settingKey] * multiplier;
          }
        }

      }
    }

    let campaignUpdate: any = {
      _id: this.campaign._id,
      name: this.campaign.name,
      live: this.campaign.live,
      subtype: this.campaign.subtype,
      inventoryAccountId: this.campaign.inventoryAccountId,
      activeStep: this.campaign.activeStep,
      sameItemOnly: this.campaign.sameItemOnly,
      allAfterThreshold: this.campaign.allAfterThreshold,
      scheduleStartEnabled: this.campaign.scheduleStartEnabled,
      scheduleEndEnabled: this.campaign.scheduleEndEnabled,
      stackable: this.campaign.stackable === true,
      applyMode: this.campaign.applyMode || 'auto',
      requireNote: this.campaign.requireNote === true,
      maxCustomerUses: this.campaign.maxCustomerUses || null,
      customerUsePerItem: this.campaign.customerUsePerItem === true,
      maxQtyPerUse: this.campaign.maxQtyPerUse || null,
      locationId: this.campaign.locationId,
      timeRange: this.campaign.timeRange,
      threshold: this.campaign.threshold,
      inventoryAccountSettings,
      addedSections: this.campaign.addedSections || [],
      scope: (this.campaign.scope || []).map(entry => ({
        type: entry.type,
        _id: entry._id,
        name: entry.name,
      })),
      scopeUnitDescription: this.campaign.scopeUnitDescription || [],
      scopeInventoryGroups: (this.campaign.scopeInventoryGroups || []).map(entry => ({
        _id: entry._id,
        name: entry.name,
      })),
      excludedInventoryGroups: (this.campaign.excludedInventoryGroups || []).map(entry => ({
        _id: entry._id,
        name: entry.name,
      })),
      userGroups: (this.campaign.userGroups || []).map(entry => ({
        _id: entry._id,
        name: entry.name,
      })),
      customerGroups: (this.campaign.customerGroups || []).map(entry => ({
        _id: entry._id,
        name: entry.name,
      })),
    };

    if (this.campaign.discountedUnitPrice) {
      campaignUpdate.discountedUnitPrice = this.campaign.discountedUnitPrice * 100;
    }

    if (this.campaign.discountedTotalPrice) {
      campaignUpdate.discountedTotalPrice = this.campaign.discountedTotalPrice * 100;
    }

    if (this.campaign.discountPercent) {
      campaignUpdate.discountPercent = this.campaign.discountPercent / 100;
    }

    // $ Off amount and optional minimum purchase, sent as whole cents; a
    // cleared minimum is sent as null so the server drops the stored one.
    campaignUpdate.amountOff = this.campaign.amountOff > 0
      ? Math.round(this.campaign.amountOff * 100) : null;
    campaignUpdate.minimumPurchase = this.campaign.minimumPurchase > 0
      ? Math.round(this.campaign.minimumPurchase * 100) : null;

    await this.ioService.post('/group/updateCampaign', campaignUpdate);
  }

  // ==== Section navigation ==========================================

  template = () => {
    return this.templates[this.campaign?.subtype] || {
      name: '', description: '',
      sections: ['basics', 'locations', 'schedule'],
      optional: [],
    };
  }

  // A restriction is optional when the template doesn't require it.
  isOptionalSection = (key): boolean => {
    return this.sections[key]?.kind === 'restriction'
      && this.template().sections.indexOf(key) === -1;
  }

  // Template sections + added/configured restrictions (before schedule) + review.
  // A restriction with data ALWAYS shows, even if never explicitly added —
  // an active restriction must never be invisible.
  visibleSections = (): string[] => {
    if (this.readOnly && this.dealHidden) {
      // The deal (and with it the type's template) is withheld: list what the
      // viewer may see — core facts plus any restriction that has data.
      const campaign = this.campaign || {};
      const viewable = ['basics', 'locations'];
      if (campaign.inventoryAccountId?.length) { viewable.push('items'); }
      for (const key of ['categoryScope', 'unitDescription', 'inventoryGroup', 'excludedInventoryGroup', 'userGroup', 'customerGroup']) {
        if (this.isSectionComplete(key)) { viewable.push(key); }
      }
      viewable.push('schedule', 'review');
      return viewable;
    }
    const template = this.template();
    const list = [...template.sections];
    const scheduleIndex = list.indexOf('schedule');
    const insertAt = scheduleIndex === -1 ? list.length : scheduleIndex;
    const added = template.optional
      .filter(key => list.indexOf(key) === -1
        && ((this.campaign?.addedSections || []).includes(key) || this.isSectionComplete(key)));
    list.splice(insertAt, 0, ...added);
    list.push('review');
    return list;
  }

  availableRestrictions = (): any[] => {
    const visible = this.visibleSections();
    return this.template().optional
      .filter(key => visible.indexOf(key) === -1)
      .map(key => this.sections[key]);
  }

  goToStep = async (stepKey) => {
    if (this.readOnly) {
      return;
    }
    this.addRestrictionOpen = false;
    this.campaign.activeStep = stepKey;
    await this.loadSectionData(stepKey);
    await this.saveUpdates();
  }

  nextStep = async () => {
    const order = this.visibleSections();
    const index = order.indexOf(this.campaign.activeStep);
    if (index > -1 && index < order.length - 1) {
      await this.goToStep(order[index + 1]);
    }
  };

  prevStep = async () => {
    const order = this.visibleSections();
    const index = order.indexOf(this.campaign.activeStep);
    if (index > 0) {
      await this.goToStep(order[index - 1]);
    }
  };

  addSection = async (sectionKey) => {
    if (this.readOnly) { return; }
    if (!this.campaign.addedSections) {
      this.campaign.addedSections = [];
    }
    if (this.campaign.addedSections.indexOf(sectionKey) === -1) {
      this.campaign.addedSections.push(sectionKey);
    }
    await this.goToStep(sectionKey);
  }

  // Removing a restriction section also clears its configuration.
  removeSection = async (sectionKey) => {
    if (this.readOnly) { return; }
    this.campaign.addedSections = (this.campaign.addedSections || []).filter(key => key !== sectionKey);
    switch (sectionKey) {
      case 'categoryScope':
        this.campaign.scope = [];
        break;
      case 'unitDescription':
        this.campaign.scopeUnitDescription = [];
        break;
      case 'inventoryGroup':
        this.campaign.scopeInventoryGroups = [];
        break;
      case 'excludedInventoryGroup':
        this.campaign.excludedInventoryGroups = [];
        break;
      case 'minimumBuy':
        this.campaign.threshold = null;
        break;
      case 'customerUseLimit':
        this.campaign.maxCustomerUses = null;
        this.campaign.customerUsePerItem = false;
        this.campaign.maxQtyPerUse = null;
        break;
      case 'userGroup':
        this.campaign.userGroups = [];
        break;
      case 'customerGroup':
        this.campaign.customerGroups = [];
        break;
    }
    if (this.campaign.activeStep === sectionKey) {
      this.campaign.activeStep = 'review';
    }
    await this.saveUpdates();
  }

  loadSectionData = async (sectionKey) => {
    switch (sectionKey) {
      case 'items':
        this.selectedUnitDescription = 'Any';
        this.getCategories();
        this.getGroups();
        break;
      case 'categoryScope':
        this.getScopeOptions();
        break;
      case 'unitDescription':
        await this.getUnitDescriptionOptions();
        break;
      case 'inventoryGroup':
      case 'excludedInventoryGroup':
        this.getInventoryGroupOptions();
        break;
      case 'userGroup':
        this.getUserGroupOptions();
        break;
      case 'customerGroup':
        this.getCustomerGroupOptions();
        break;
      case 'amount': {
        if (this.campaign.subtype === 'discountedMultiples' && !this.campaign.sameItemOnly) {
          this.updateItemCalculations();
        }
        const editable = this.amountColumns().filter(column => column.editable);
        if (!editable.some(column => column.key === this.bulkColumnKey)) {
          this.bulkColumnKey = editable[0]?.key || '';
        }
        this.bulkValue = null;
        break;
      }
      case 'schedule':
        this.scheduleStart = moment(this.rangeDate(this.campaign.timeRange?.startDate) || new Date());
        this.scheduleEnd = moment(this.rangeDate(this.campaign.timeRange?.endDate) || new Date());
        break;
      case 'review':
        this.scopeItemCount = null;
        // Scope-less %-off and $-off campaigns are storewide: count everything in range.
        if (this.campaign.scope?.length || ['categoryPercent', 'amountOff'].includes(this.campaign.subtype)) {
          this.ioService.post('/inventory/getScopeItemCount', {
            scope: this.campaign.scope,
            scopeUnitDescription: this.campaign.scopeUnitDescription || [],
            scopeInventoryGroupIds: (this.campaign.scopeInventoryGroups || []).map(entry => entry._id),
            excludedInventoryGroupIds: (this.campaign.excludedInventoryGroups || []).map(entry => entry._id),
          }).then((response: any) => {
            this.scopeItemCount = response?.count ?? null;
          }).catch(() => {});
        }
        break;
    }
  }

  inlineRangeUpdated = async ($event) => {
    if (!$event?.startDate || !$event?.endDate) {
      return;
    }
    this.scheduleStart = $event.startDate;
    this.scheduleEnd = $event.endDate;
    this.campaign.timeRange = {
      startDate: $event.startDate.toDate(),
      endDate: $event.endDate.toDate(),
    };
    await this.saveUpdates();
  }

  // ==== Section status ==============================================

  isSectionComplete = (sectionKey): boolean => {
    const campaign = this.campaign;
    if (!campaign) { return false; }
    switch (sectionKey) {
      case 'basics':
        return !!campaign.name && !!campaign.subtype;
      case 'locations':
        return campaign.locationId?.length > 0;
      case 'items':
        return campaign.inventoryAccountId?.length > 0;
      case 'categoryScope':
        return campaign.scope?.length > 0;
      case 'unitDescription':
        return campaign.scopeUnitDescription?.length > 0;
      case 'inventoryGroup':
        return campaign.scopeInventoryGroups?.length > 0;
      case 'excludedInventoryGroup':
        return campaign.excludedInventoryGroups?.length > 0;
      case 'userGroup':
        return campaign.userGroups?.length > 0;
      case 'customerGroup':
        return campaign.customerGroups?.length > 0;
      case 'customerUseLimit':
        return campaign.maxCustomerUses > 0;
      case 'minimumBuy':
        // As a restriction, "configured" means a real minimum above 1.
        return campaign.threshold > 1;
      case 'minimumPurchase':
        return campaign.minimumPurchase > 0;
      case 'amount':
        switch (campaign.subtype) {
          case 'categoryPercent':
            return campaign.discountPercent > 0;
          case 'amountOff':
            return campaign.amountOff > 0;
          case 'buyOneGetOne':
          case 'buyOneGetOneHalfOff':
            return true; // fixed by the template
          case 'discountedMultiples':
            if (campaign.sameItemOnly) {
              // Every item must be fully defined — the server skips
              // incomplete ones silently at expansion.
              return campaign.inventoryAccounts?.length > 0
                && campaign.inventoryAccounts.every(item =>
                  item.discountSettings?.discountedUnitPrice && item.discountSettings?.threshold);
            }
            return !!campaign.discountedUnitPrice;
          default: // salePrice
            return campaign.inventoryAccounts?.length > 0
              && campaign.inventoryAccounts.every(item => item.discountSettings?.salePrice);
        }
      case 'schedule':
        // Each enabled side needs its date; disabled sides are open.
        return (!campaign.scheduleStartEnabled || !!campaign.timeRange?.startDate)
          && (!campaign.scheduleEndEnabled || !!campaign.timeRange?.endDate);
      default:
        return true;
    }
  }

  sectionSummary = (sectionKey): string => {
    const campaign = this.campaign;
    if (!campaign) { return ''; }
    switch (sectionKey) {
      case 'basics':
        return campaign.name || '';
      case 'locations':
        return campaign.locationId?.length ? campaign.locationId.length + ' selected' : '';
      case 'items':
        return campaign.inventoryAccountId?.length ? campaign.inventoryAccountId.length + ' items' : '';
      case 'categoryScope':
        return (campaign.scope || []).map(entry => entry.name).join(', ');
      case 'unitDescription':
        return (campaign.scopeUnitDescription || []).join(', ');
      case 'inventoryGroup':
        return (campaign.scopeInventoryGroups || []).map(entry => entry.name).join(', ');
      case 'excludedInventoryGroup':
        return (campaign.excludedInventoryGroups || []).map(entry => entry.name).join(', ');
      case 'userGroup':
        return (campaign.userGroups || []).map(entry => entry.name).join(', ');
      case 'customerGroup':
        return (campaign.customerGroups || []).map(entry => entry.name).join(', ');
      case 'customerUseLimit': {
        if (!(campaign.maxCustomerUses > 0)) { return ''; }
        let limitSummary = campaign.maxCustomerUses
          + (campaign.customerUsePerItem ? ' per product' : '') + ' per customer';
        if (campaign.customerUsePerItem && campaign.maxQtyPerUse > 0) {
          limitSummary += ' (max ' + campaign.maxQtyPerUse + '/use)';
        }
        return limitSummary;
      }
      case 'minimumBuy':
        if (campaign.subtype === 'discountedMultiples' && campaign.sameItemOnly) {
          return 'per item';
        }
        return campaign.threshold > 1 ? String(campaign.threshold) : '';
      case 'minimumPurchase':
        return campaign.minimumPurchase > 0 ? '$' + this.dollars(campaign.minimumPurchase) + ' or more' : '';
      case 'amount':
        switch (campaign.subtype) {
          case 'categoryPercent':
            return campaign.discountPercent ? campaign.discountPercent + '% off' : '';
          case 'amountOff':
            return campaign.amountOff > 0 ? '$' + this.dollars(campaign.amountOff) + ' off' : '';
          case 'buyOneGetOne':
            return '1 free';
          case 'buyOneGetOneHalfOff':
            return '1 half off';
          case 'discountedMultiples': {
            if (!campaign.sameItemOnly) {
              return campaign.discountedUnitPrice ? '$' + campaign.discountedUnitPrice + ' each' : '';
            }
            const configured = (campaign.inventoryAccounts || []).filter(item =>
              item.discountSettings?.discountedUnitPrice && item.discountSettings?.threshold).length;
            return configured + '/' + (campaign.inventoryAccounts?.length || 0) + ' configured';
          }
          default: {
            const priced = (campaign.inventoryAccounts || []).filter(item =>
              item.discountSettings?.salePrice).length;
            return priced + '/' + (campaign.inventoryAccounts?.length || 0) + ' priced';
          }
        }
      case 'stacking':
        return campaign.stackable ? 'Stackable' : 'Not stackable';
      case 'application': {
        const modeLabels = { auto: 'Automatic', manual: 'Manual', manualItem: 'Manual per item' };
        let applicationSummary = modeLabels[campaign.applyMode] || 'Automatic';
        if (campaign.applyMode !== 'auto' && campaign.requireNote) {
          applicationSummary += ' · note required';
        }
        return applicationSummary;
      }
      case 'schedule': {
        const start = campaign.scheduleStartEnabled && campaign.timeRange?.startDate
          ? new Date(campaign.timeRange.startDate).toLocaleDateString() : null;
        const end = campaign.scheduleEndEnabled && campaign.timeRange?.endDate
          ? new Date(campaign.timeRange.endDate).toLocaleDateString() : null;
        if (start && end) { return start + ' - ' + end; }
        if (start) { return 'From ' + start; }
        if (end) { return 'Until ' + end; }
        return 'Always on';
      }
      default:
        return '';
    }
  }

  // Sidebar variant: long entity lists compress to counts so the nav never
  // grows past its fixed width.
  sectionSummaryShort = (sectionKey): string => {
    const campaign = this.campaign;
    const listSections: any = {
      categoryScope: [campaign?.scope, 'selected'],
      unitDescription: [campaign?.scopeUnitDescription, 'sizes'],
      inventoryGroup: [campaign?.scopeInventoryGroups, 'groups'],
      excludedInventoryGroup: [campaign?.excludedInventoryGroups, 'groups'],
      userGroup: [campaign?.userGroups, 'groups'],
      customerGroup: [campaign?.customerGroups, 'groups'],
    };
    const entry = listSections[sectionKey];
    if (entry && Array.isArray(entry[0]) && entry[0].length > 2) {
      return entry[0].length + ' ' + entry[1];
    }
    return this.sectionSummary(sectionKey);
  }

  readyToGoLive = (): boolean => {
    return this.visibleSections()
      .filter(key => key !== 'review')
      .every(key => this.isOptionalSection(key) || this.isSectionComplete(key));
  }

  selectSubtype = (subtypeKey) => {
    const previous = this.campaign.subtype;
    this.campaign.subtype = subtypeKey;
    // A $ Off campaign is usually a coupon the cashier hands out on top of
    // whatever else applies, so it starts manual and stackable; both remain
    // editable in the Application and Stacking steps.
    if (subtypeKey === 'amountOff' && previous !== 'amountOff') {
      if (!this.campaign.applyMode || this.campaign.applyMode === 'auto') {
        this.campaign.applyMode = 'manual';
      }
      this.campaign.stackable = true;
    }
  }

  // Whole-dollar-and-cents label for an amount edited in dollars.
  dollars = (amount): string => {
    return (Math.round(amount * 100) / 100).toFixed(2);
  }

  // ==== Review page =================================================

  // The campaign as one plain-English sentence.
  reviewHeadline = (): string => {
    const campaign = this.campaign;
    if (!campaign) { return ''; }
    const itemCount = campaign.inventoryAccountId?.length || 0;
    const clauses: string[] = [];

    switch (campaign.subtype) {
      case 'categoryPercent':
        clauses.push((campaign.discountPercent || '?') + '% off every qualifying item'
          + (campaign.threshold > 1 ? ' when buying ' + campaign.threshold + ' or more' : '')
          + (campaign.scope?.length ? '' : ' storewide'));
        break;
      case 'amountOff':
        clauses.push('$' + (campaign.amountOff > 0 ? this.dollars(campaign.amountOff) : '?') + ' off the purchase'
          + (campaign.minimumPurchase > 0 ? ' on $' + this.dollars(campaign.minimumPurchase) + ' or more' : '')
          + (campaign.scope?.length ? '' : ' storewide'));
        break;
      case 'buyOneGetOne':
        clauses.push('Buy one, get one free across ' + itemCount + ' item' + (itemCount === 1 ? '' : 's')
          + (campaign.sameItemOnly ? ' (same item only)' : ''));
        break;
      case 'buyOneGetOneHalfOff':
        clauses.push('Buy one, get one half off across ' + itemCount + ' item' + (itemCount === 1 ? '' : 's')
          + (campaign.sameItemOnly ? ' (same item only)' : ''));
        break;
      case 'discountedMultiples':
        if (campaign.sameItemOnly) {
          clauses.push('Per-item multiple pricing on ' + itemCount + ' item' + (itemCount === 1 ? '' : 's'));
        } else {
          clauses.push('Any ' + (campaign.threshold || '?') + ' of ' + itemCount + ' items for $'
            + (campaign.discountedUnitPrice || '?') + ' each');
        }
        break;
      default: // salePrice
        clauses.push('Fixed sale prices on ' + itemCount + ' item' + (itemCount === 1 ? '' : 's'));
        break;
    }

    if (campaign.scope?.length) {
      clauses.push('within ' + this.scopeSummary());
    }
    if (campaign.scopeUnitDescription?.length) {
      clauses.push(campaign.scopeUnitDescription.join(', ') + ' only');
    }
    if (campaign.scopeInventoryGroups?.length) {
      clauses.push('in ' + campaign.scopeInventoryGroups.map(entry => entry.name).join(', '));
    }
    if (campaign.excludedInventoryGroups?.length) {
      clauses.push('excluding ' + campaign.excludedInventoryGroups.map(entry => entry.name).join(', '));
    }
    if (campaign.userGroups?.length) {
      clauses.push('rung up by ' + campaign.userGroups.map(entry => entry.name).join(', '));
    }
    if (campaign.customerGroups?.length) {
      clauses.push('for ' + campaign.customerGroups.map(entry => entry.name).join(', ') + ' customers');
    }
    if (campaign.stackable) {
      clauses.push('stacks with other discounts');
    }
    if (campaign.applyMode === 'manual' || campaign.applyMode === 'manualItem') {
      clauses.push('added manually' + (campaign.applyMode === 'manualItem' ? ' per item' : '')
        + (campaign.requireNote ? ', note required' : ''));
    }
    if (campaign.maxCustomerUses > 0) {
      let limitClause = 'limit ' + campaign.maxCustomerUses
        + (campaign.customerUsePerItem ? ' per product' : '') + ' per customer';
      if (campaign.customerUsePerItem && campaign.maxQtyPerUse > 0) {
        limitClause += ' (up to ' + campaign.maxQtyPerUse + ' units each use)';
      }
      clauses.push(limitClause);
    }

    const locationCount = campaign.locationId?.length || 0;
    clauses.push('at ' + locationCount + ' location' + (locationCount === 1 ? '' : 's'));

    let sentence = clauses.join(' — ');
    const window = this.scheduleWindow();
    if (window.start && window.end) {
      sentence += ', ' + this.scheduleDateLabel(window.start) + ' to ' + this.scheduleDateLabel(window.end);
    } else if (window.start) {
      sentence += ', starting ' + this.scheduleDateLabel(window.start);
    } else if (window.end) {
      sentence += ', until ' + this.scheduleDateLabel(window.end);
    }
    return sentence + '.';
  }

  reviewWarningCounts = () => {
    const items = this.amountHasTable() ? (this.campaign?.inventoryAccounts || []) : [];
    return {
      belowCost: items.filter(item => this.hasNegativeMargin(item)).length,
      noop: items.filter(item => this.hasZeroDiscount(item)).length,
      collisions: (this.campaign?.inventoryAccounts || [])
        .filter(item => this.campaign?.collisions?.[item._id]?.length > 0).length,
    };
  }

  incompleteSections = (): string[] => {
    return this.visibleSections()
      .filter(key => key !== 'review' && !this.isOptionalSection(key) && !this.isSectionComplete(key));
  }

  previewFinalPrice = (item): string => {
    switch (this.campaign?.subtype) {
      case 'salePrice':
        return item.discountSettings?.salePrice ? '$' + item.discountSettings.salePrice : '';
      case 'discountedMultiples':
        if (this.campaign.sameItemOnly) {
          return item.discountSettings?.discountedUnitPrice
            ? (item.discountSettings.threshold || '?') + '+ @ $' + item.discountSettings.discountedUnitPrice
            : '';
        }
        return this.campaign.discountedUnitPrice ? '$' + this.campaign.discountedUnitPrice + ' each' : '';
      default:
        return '';
    }
  }

  collisionNames = (item): string => {
    return (this.campaign?.collisions?.[item._id] || []).map(collision => collision.name).join(', ');
  }

  duplicateCampaign = async () => {
    if (this.readOnly) { return; }
    const response: any = await this.ioService.post('/group/duplicateCampaign', {
      _id: this.campaign._id,
    }).catch(() => null);
    if (response?._id) {
      AppComponent.showSnack('Campaign duplicated — now editing the copy.', 'OK', 4000);
      this.router.navigate(['../', response._id], { relativeTo: this.route });
    } else {
      AppComponent.showSnack('Duplicate failed.', 'OK', 4000);
    }
  }

  // ==== Item selection (Items section) ==============================

  getSelectedItems = async () => {
    if (!this.campaign.inventoryAccountId) {
      this.campaign.inventoryAccountId = [];
    }

    this.campaign.inventoryAccounts = [];

    let items: any;

    if (this.campaign.inventoryAccountId.length > 0) {
      items = await this.ioService.post('/inventory/findInventoryAccounts', {
        _id: this.campaign.inventoryAccountId,
        sort: { name: 1 },
        additionalFields: this.itemFields.map(field => field.key),
      });
    }

    if (items?.inventoryAccounts.length > 0) {
      this.indexedItems = this.arrayToObject(items.inventoryAccounts, '_id');
      for (let item of this.campaign.inventoryAccountId) {
        let itemDetail = this.indexedItems[item];
        if (itemDetail) {
          if (!itemDetail.discountSettings) {
            itemDetail.discountSettings = {};
          }
          this.campaign.inventoryAccounts.push(itemDetail);
        }
      }
    }
  };

  locationName = (id) => {
    return (this.locations || []).find(location => location._id === id)?.name || id;
  }

  clearLocations = () => {
    this.campaign.locationId = [];
  }

  toggleOption = (fieldKey, optionKey) => {
    if (this.campaign[fieldKey].indexOf(optionKey) !== -1) {
      this.campaign[fieldKey].splice(this.campaign[fieldKey].indexOf(optionKey), 1);
    } else {
      this.campaign[fieldKey].push(optionKey);
    }
  };

  updateSearch = async (event: any) => {
    this.searchText = event.target.value;
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    this.searchTimeout = setTimeout(async () => {
      this.getItems();
    }, 800);
  };

  getItems = async () => {
    let itemsFind: any = {
      subtype: 'retail',
    };

    if (this.searchText) {
      itemsFind.search = this.searchText;

      itemsFind.searchConfig = {
        fuseOptions: {
          threshold: 0.0,
          keys: [
            'name',
            'sku',
            'barcode'
          ]
        }
      };

    } else {
      itemsFind.sort = { name: 1 };
    }

    if (this.selectedCategory) {
      itemsFind.categoryId = this.selectedCategory;
    }

    if (this.selectedGroup) {
      itemsFind.groupId = this.selectedGroup;
    }

    if (this.selectedUnitDescription && this.selectedUnitDescription !== 'Any') {
      itemsFind.unitDescription = this.selectedUnitDescription;
    }

    if (this.itemFields.length > 0) {
      itemsFind.additionalFields = this.itemFields.map(field => field.key);

      const fieldFilters: any = {};
      for (const field of this.itemFields) {
        const value = this.selectedItemFields[field.key];
        if (value && value !== 'Any') {
          fieldFilters[field.key] = value;
        }
      }
      if (Object.keys(fieldFilters).length > 0) {
        itemsFind.fieldFilters = fieldFilters;
      }
    }

    let searchFilterResponse: any = await this.ioService.post('/inventory/findInventoryAccounts', itemsFind);

    let unitDescription = [];

    let itemFieldValues: any = {};
    for (const field of this.itemFields) {
      itemFieldValues[field.key] = [];
    }

    let resultIds = [];
    for (let item of searchFilterResponse.inventoryAccounts) {
      if (this.campaign.inventoryAccountId && this.campaign.inventoryAccountId.indexOf(item._id) > -1) {
        item.active = true;
      }
      if (item.unitDescription && unitDescription.indexOf(item.unitDescription) === -1) {
        unitDescription.push(item.unitDescription);
      }
      for (const field of this.itemFields) {
        if (item[field.key] && itemFieldValues[field.key].indexOf(item[field.key]) === -1) {
          itemFieldValues[field.key].push(item[field.key]);
        }
      }

      if (!item.discountSettings) {
        item.discountSettings = {};
      }

      resultIds.push(item._id);
    }

    if (unitDescription.length > 0) {
      unitDescription.sort();
      unitDescription.unshift('Any');
      this.unitDescription = unitDescription;
    } else {
      this.unitDescription = ['Any'];
    }

    for (const field of this.itemFields) {
      const values = itemFieldValues[field.key];
      values.sort();
      values.unshift('Any');
      this.itemFieldOptions[field.key] = values;
    }

    this.results = searchFilterResponse.inventoryAccounts;
    this.resultIds = resultIds;

    if (this.results.length === 0 && (this.selectedUnitDescription !== 'Any' || this.hasActiveItemFieldFilter())) {
      this.selectedUnitDescription = 'Any';
      for (const field of this.itemFields) {
        this.selectedItemFields[field.key] = 'Any';
      }
      this.getItems();
    }

  };

  hasActiveItemFieldFilter = () => {
    return this.itemFields.some(field => {
      const value = this.selectedItemFields[field.key];
      return value && value !== 'Any';
    });
  }

  getCategories = async () => {
    let categories: any = await this.ioService.post('/group/getGroups', {
      type: 'inventory',
      subtype: 'category',
      anyLocation: true
    });

    categories.unshift({
      name: 'Any',
      _id: ''
    });

    this.categories = categories;
  }

  getGroups = async () => {
    let groups: any = await this.ioService.post('/group/getGroups', {
      type: 'inventory',
      subtype: 'group',
      anyLocation: true
    });

    groups.unshift({
      name: 'Any',
      _id: ''
    });

    this.groups = groups;
  }

  addOption = (option) => {
    if (!option.active) {
      this.campaign.inventoryAccounts.push(option);
      if (!this.campaign.inventoryAccountId) {
        this.campaign.inventoryAccountId = [];
      }

      option.active = true;

      this.campaign.inventoryAccountId.push(option._id);
    }
  }

  removeOption = (option) => {
    this.campaign.inventoryAccounts.splice(this.campaign.inventoryAccounts.indexOf(option), 1);
    this.campaign.inventoryAccountId.splice(this.campaign.inventoryAccountId.indexOf(option._id), 1);

    let resultIndex = this.resultIds.indexOf(option._id);
    if (resultIndex > -1) {
      this.results[resultIndex].active = false;
    }

  }

  addAll = () => {
    for (let option of this.results) {
      this.addOption(option);
    }
  }

  clearSelected = () => {
    this.campaign.inventoryAccounts = [];
    this.campaign.inventoryAccountId = [];
    for (let option of this.results) {
      option.active = false;
    }
  }

  // ==== Category / subcategory scope section ========================

  // Categories and subcategories are both inventory Groups with
  // subtype 'category'; a subcategory carries a parentId. Built into a
  // category tree for the drill-down scope picker.
  getScopeOptions = async () => {
    const groups: any = await this.ioService.post('/group/getGroups', {
      type: 'inventory',
      subtype: 'category',
      anyLocation: true,
      anyParent: true
    });

    const subcategories = groups.filter(group => group.parentId);

    this.scopeCategories = groups.filter(group => !group.parentId).map(category => {
      category.subcategories = subcategories.filter(sub => sub.parentId === category._id);
      return category;
    });

    if (this.activeScopeCategory) {
      this.activeScopeCategory = this.scopeCategories
        .find(category => category._id === this.activeScopeCategory._id) || null;
    }
  }

  activateScopeCategory = (category) => {
    this.activeScopeCategory = category;
  }

  isScopeSelected = (id) => {
    return !!this.campaign.scope?.some(entry => entry._id === id);
  }

  toggleScopeCategory = (category) => {
    if (!this.campaign.scope) {
      this.campaign.scope = [];
    }

    const index = this.campaign.scope.findIndex(entry => entry._id === category._id);
    if (index > -1) {
      this.campaign.scope.splice(index, 1);
    } else {
      // The whole category includes its subcategories; drop now-redundant ones.
      this.campaign.scope = this.campaign.scope.filter(entry =>
        !category.subcategories?.some(sub => sub._id === entry._id));
      this.campaign.scope.push({
        type: 'categoryId',
        _id: category._id,
        name: category.name,
      });
    }
  }

  toggleScopeSubcategory = (subcategory, parent) => {
    if (!this.campaign.scope) {
      this.campaign.scope = [];
    }

    if (this.isScopeSelected(parent._id)) {
      // Already included via the whole category.
      return;
    }

    const index = this.campaign.scope.findIndex(entry => entry._id === subcategory._id);
    if (index > -1) {
      this.campaign.scope.splice(index, 1);
    } else {
      this.campaign.scope.push({
        type: 'subcategoryId',
        _id: subcategory._id,
        name: parent.name + ' > ' + subcategory.name,
      });
    }
  }

  removeScope = (entry) => {
    this.campaign.scope.splice(this.campaign.scope.indexOf(entry), 1);
  }

  clearScope = () => {
    this.campaign.scope = [];
  }

  scopeSummary = (): string => {
    return (this.campaign?.scope || []).map(entry => entry.name).join(', ');
  }

  // ==== Container size section ======================================

  getUnitDescriptionOptions = async () => {
    this.unitDescriptionOptions = (await this.ioService.post('/inventory/getUnitDescriptions', {
      // Only sizes that exist on items within the selected categories/subcategories.
      scope: this.campaign.scope,
    })) || [];
  }

  filteredUnitOptions = () => {
    if (!this.unitSearchText) {
      return this.unitDescriptionOptions;
    }
    const search = this.unitSearchText.toLowerCase();
    return this.unitDescriptionOptions.filter(value => value.toLowerCase().includes(search));
  }

  toggleUnitDescription = (value) => {
    const index = this.campaign.scopeUnitDescription.indexOf(value);
    if (index > -1) {
      this.campaign.scopeUnitDescription.splice(index, 1);
    } else {
      this.campaign.scopeUnitDescription.push(value);
    }
  }

  clearUnitDescriptions = () => {
    this.campaign.scopeUnitDescription = [];
  }

  // ==== Group-list restriction sections =============================

  getInventoryGroupOptions = async () => {
    this.inventoryGroupOptions = await this.ioService.post('/group/getGroups', {
      type: 'inventory',
      subtype: 'group',
      anyLocation: true
    });
  }

  // /userGroup/getUserGroups needs admin, which a store owner editing
  // campaigns need not have; the swallowed 403 showed an empty list. Take the
  // user groups from the campaign-permission endpoint instead (its "user"
  // source) and show a load failure in the step.
  getUserGroupOptions = async () => {
    this.userGroupError = '';
    try {
      const options: any = await this.ioService.post('/group/getCustomerGroupOptions', {});
      this.userGroupOptions = (Array.isArray(options) ? options : []).filter(option => option.source === 'user');
    } catch (e) {
      this.userGroupOptions = [];
      this.userGroupError = (e && e.error && e.error.msg) || (e && e.msg) || 'Could not load the user groups.';
    }
  }

  // The attached customer may be a staff user (employee discounts), so user
  // groups are valid targets alongside dedicated customer groups.
  // Customer groups plus user groups (an employee discount restricts to the
  // Employee user group), from one campaign-permission endpoint so a store
  // owner without admin rights gets the same list. A failure is shown in the
  // step rather than passing for "no groups defined".
  getCustomerGroupOptions = async () => {
    this.customerGroupError = '';
    try {
      const options: any = await this.ioService.post('/group/getCustomerGroupOptions', {});
      this.customerGroupOptions = Array.isArray(options) ? options : [];
    } catch (e) {
      this.customerGroupOptions = [];
      this.customerGroupError = (e && e.error && e.error.msg) || (e && e.msg) || 'Could not load the customer groups.';
    }
  }

  isEntitySelected = (listKey, id) => {
    return !!this.campaign[listKey]?.some(entry => entry._id === id);
  }

  toggleEntity = (listKey, option) => {
    if (!Array.isArray(this.campaign[listKey])) {
      this.campaign[listKey] = [];
    }
    const index = this.campaign[listKey].findIndex(entry => entry._id === option._id);
    if (index > -1) {
      this.campaign[listKey].splice(index, 1);
    } else {
      this.campaign[listKey].push({ _id: option._id, name: option.name });
    }
  }

  clearEntities = (listKey) => {
    this.campaign[listKey] = [];
  }

  // ==== Amount / calculation helpers (unchanged math) ===============

  // Only these types edit a per-item table locally; others save on navigation.
  amountHasTable = (): boolean => {
    return ['salePrice', 'discountedMultiples'].includes(this.campaign?.subtype);
  }

  // Row warnings: selling below cost, or a "discount" that takes nothing off.
  hasNegativeMargin = (item): boolean => {
    const margin = parseFloat(item.discountSettings?.margin);
    return !isNaN(margin) && margin < 0;
  }

  hasZeroDiscount = (item): boolean => {
    // Judge by the defining price, not the stored discountAmount — zero
    // values are dropped in the save/load round-trip, so a no-op discount
    // can present as blank. A sale price at or above list discounts nothing.
    const priceKey = this.campaign?.subtype === 'discountedMultiples' ? 'discountedUnitPrice' : 'salePrice';
    const value = parseFloat(item.discountSettings?.[priceKey]);
    if (!isNaN(value)) {
      return value * 100 >= item.price;
    }
    const discount = parseFloat(item.discountSettings?.discountAmount);
    return !isNaN(discount) && discount <= 0;
  }

  bulkColumns = () => {
    return this.amountColumns().filter(column => column.editable);
  }

  // Fill one editable column across the table; each item runs through the
  // normal field-update cascade so dependent columns recalculate.
  applyBulk = (onlyUndefined = false) => {
    const column = this.amountColumns().find(c => c.key === this.bulkColumnKey && c.editable);
    if (!column || this.bulkValue == null || this.bulkValue === '') {
      return;
    }
    for (const item of this.campaign.inventoryAccounts || []) {
      const current = item.discountSettings?.[column.key];
      if (onlyUndefined && current !== undefined && current !== null && current !== '') {
        continue;
      }
      this.itemFieldUpdated(item, column, this.bulkValue);
    }
  }

  amountColumns = () => {
    switch (this.campaign.subtype) {
      case 'discountedMultiples':
        return this.campaign.sameItemOnly ? this.discountedMultiplesColumns : this.discountedMultiplesAnyItemColumns;
      default:
        return this.salePriceColumns;
    }
  }

  fieldTab = (fieldKey, itemIndex) => {
    itemIndex++;

    if (itemIndex < this.campaign.inventoryAccounts.length) {
      setTimeout(() => {
        const targetInput = document.getElementById('item-'+itemIndex+'-'+fieldKey) as HTMLInputElement;
        targetInput.focus();
        targetInput.select();
      }, 0);
    }

  }

  rangeChanged = (event) => {
    console.log('rangeChanged: ', event);
  }

  // timeRange dates are Date objects on load but daterangepicker moments
  // after an edit — normalize before doing math or formatting.
  private rangeDate = (value): Date => {
    if (!value) { return null; }
    return new Date(value.toDate ? value.toDate() : value);
  }

  // Enabled-side dates only; a disabled side is an open end.
  private scheduleWindow = () => {
    return {
      start: this.campaign?.scheduleStartEnabled ? this.rangeDate(this.campaign?.timeRange?.startDate) : null,
      end: this.campaign?.scheduleEndEnabled ? this.rangeDate(this.campaign?.timeRange?.endDate) : null,
    };
  }

  scheduleStatus = (): string => {
    const { start, end } = this.scheduleWindow();
    if (!start && !end) { return 'Always on'; }
    const now = new Date();
    if (start && now < start) { return 'Upcoming'; }
    if (end && now > end) { return 'Ended'; }
    return 'Active now';
  }

  scheduleDuration = (): string => {
    const { start, end } = this.scheduleWindow();
    if (!start || !end) { return 'Open-ended'; }
    const days = Math.round((end.getTime() - start.getTime()) / 86400000);
    return days === 1 ? '1 day' : days + ' days';
  }

  scheduleStartLabel = (): string => {
    const { start } = this.scheduleWindow();
    return start ? this.scheduleDateLabel(start) : 'Immediately';
  }

  scheduleEndLabel = (): string => {
    const { end } = this.scheduleWindow();
    return end ? this.scheduleDateLabel(end) : 'Until turned off';
  }

  scheduleDateLabel = (value): string => {
    const date = this.rangeDate(value);
    if (!date) { return ''; }
    return date.toLocaleString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  }

  anyItemFieldUpdated(fieldKey) {
    switch (fieldKey) {
      case 'threshold':
      case 'discountedUnitPrice':
        if (this.campaign.threshold && this.campaign.discountedUnitPrice) {
          this.campaign.discountedTotalPrice = this.campaign.threshold * this.campaign.discountedUnitPrice;
        }
        this.updateItemCalculations();
        break;

      case 'discountedTotalPrice':
        if (this.campaign.threshold && this.campaign.discountedTotalPrice) {
          this.campaign.discountedUnitPrice = this.campaign.discountedTotalPrice / this.campaign.threshold;
        }
        this.updateItemCalculations();
        break;
    }
  }

  updateItemCalculations() {
    for (let item of this.campaign.inventoryAccounts) {
      item.discountSettings.discountAmount = item.price - (this.campaign.discountedUnitPrice*100);
      item.discountSettings.discountPercent = item.discountSettings.discountAmount / item.price;
      item.discountSettings.margin = ((this.campaign.discountedUnitPrice*100) - item.cost);
      item.discountSettings.marginPercent = item.discountSettings.margin / this.campaign.discountedUnitPrice/100;
    }
  }

  removeItem(item) {
    this.campaign.inventoryAccounts.splice(this.campaign.inventoryAccounts.indexOf(item), 1);
    this.campaign.inventoryAccountId.splice(this.campaign.inventoryAccountId.indexOf(item._id), 1);
  }

  itemFieldUpdated(item, field, $event) {
    if (!item.discountSettings) {
      item.discountSettings = {};
    }
    item.discountSettings[field.key] = $event;

    if (!item.cost) {
      item.cost = 0;
    }

    switch (field.key) {
      case 'discountedUnitPrice':
        if (item.discountSettings.threshold && item.discountSettings.discountedUnitPrice) {
          item.discountSettings.discountedTotalPrice = (item.discountSettings.threshold * item.discountSettings.discountedUnitPrice).toFixed(2);
          this.updateCalculatedFields(item);
        }
        break;

        case 'threshold':
          if (item.discountSettings.threshold && item.discountSettings.discountedUnitPrice) {
            item.discountSettings.discountedTotalPrice = (item.discountSettings.threshold * item.discountSettings.discountedUnitPrice).toFixed(2);
            this.updateCalculatedFields(item);
          }
          break;

        case 'discountedTotalPrice':
          if (item.discountSettings.threshold && item.discountSettings.discountedTotalPrice) {
            item.discountSettings.discountedUnitPrice = (item.discountSettings.discountedTotalPrice / item.discountSettings.threshold);
            this.updateCalculatedFields(item, true);
          }
          break;

        case 'salePrice':
          item.discountSettings.discountAmount = Number((item.price - (item.discountSettings.salePrice * 100)) / 100).toFixed(2);
          item.discountSettings.discountPercent = (item.discountSettings.discountAmount / item.price * 10000).toFixed(2);
          item.discountSettings.margin = Number(((item.discountSettings.salePrice * 100) - item.cost) / 100).toFixed(2);
          item.discountSettings.marginPercent = Number(item.discountSettings.margin / item.discountSettings.salePrice * 100).toFixed(2);
          break;

        case 'discountAmount':
          item.discountSettings.discountPercent = Number(item.discountSettings.discountAmount / item.price * 10000).toFixed(2);
          item.discountSettings.salePrice = Number((item.price - (item.discountSettings.discountAmount * 100)) / 100).toFixed(2);
          item.discountSettings.margin = Number(((item.discountSettings.salePrice * 100) - item.cost) / 100).toFixed(2);
          item.discountSettings.marginPercent = Number(item.discountSettings.margin / item.discountSettings.salePrice * 100).toFixed(2);
          break;

        case 'discountPercent':
          item.discountSettings.discountAmount = Number(item.price * item.discountSettings.discountPercent / 100 / 100).toFixed(2);
          item.discountSettings.salePrice = Number((item.price - (item.discountSettings.discountAmount * 100)) / 100).toFixed(2);
          item.discountSettings.margin = Number(((item.discountSettings.salePrice * 100) - item.cost) / 100).toFixed(2);
          item.discountSettings.marginPercent = Number(item.discountSettings.margin / item.discountSettings.salePrice * 100).toFixed(2);
          break;

        case 'margin':
          item.discountSettings.salePrice = Number((item.cost + (item.discountSettings.margin * 100)) / 100).toFixed(2);
          item.discountSettings.discountAmount = Number((item.price - (item.discountSettings.salePrice * 100)) / 100).toFixed(2);
          item.discountSettings.discountPercent = Number(item.discountSettings.discountAmount / item.price * 10000).toFixed(2);
          item.discountSettings.marginPercent = Number(item.discountSettings.margin / item.discountSettings.salePrice * 100).toFixed(2);
          break;

        case 'marginPercent':
          item.discountSettings.salePrice = Number((item.cost / (1 - (item.discountSettings.marginPercent / 100))) / 100).toFixed(2);
          item.discountSettings.discountAmount = Number((item.price - (item.discountSettings.salePrice * 100)) / 100).toFixed(2);
          item.discountSettings.discountPercent = Number(item.discountSettings.discountAmount / item.price * 10000).toFixed(2);
          item.discountSettings.margin = Number(((item.discountSettings.salePrice * 100) - item.cost) / 100).toFixed(2);
          break;
    }

  }

  updateCalculatedFields = (item, allowFractionalPennyDiscount = false) => {
    if (!item.discountSettings) {
      item.discountSettings = {};
    }

    if (allowFractionalPennyDiscount) {
      item.discountSettings.discountAmount = (item.price - (item.discountSettings.discountedUnitPrice*100));
    } else {
      item.discountSettings.discountAmount = (item.price - (item.discountSettings.discountedUnitPrice*100)).toFixed(2);
    }

    item.discountSettings.discountAmount = (item.price - (item.discountSettings.discountedUnitPrice*100));
    item.discountSettings.discountPercent = (item.discountSettings.discountAmount / item.price).toFixed(2);
    item.discountSettings.margin = ((item.discountSettings.discountedUnitPrice*100) - item.cost).toFixed(2);
    item.discountSettings.marginPercent = (item.discountSettings.margin / item.discountSettings.discountedUnitPrice/100).toFixed(2);
  };

  // ==== Lifecycle actions ===========================================

  toggleLive = async () => {
    if (this.readOnly) { return; }
    if (!this.campaign.live && !this.readyToGoLive()) {
      AppComponent.showSnack('Not published — complete the marked sections first.', 'OK', 4000);
      return;
    }
    this.campaign.live = !this.campaign.live;
    await this.saveUpdates();
  }

  reset = async () => {
    this.getCampaign();
  }

  deleteSaleCampaign = async () => {
    if (this.readOnly) { return; }
    if (confirm("Are you sure you want to delete this campaign?")) {
      let campaignDelete: any = {
        _id: this.campaign._id,
      };

      let deleteResponse = await this.ioService.post('/group/deleteCampaign', campaignDelete);

      this.close();
    }
  }

  // "Create Tag List": a Price Tags transfer named after the campaign with every
  // covered item at Print Qty 1, then open it so the store can print. The list
  // belongs to the store selected in the header (its Store OH Qty column reads
  // that store's stock); at "All Locations" the header picker opens instead.
  creatingTagList = false;
  async createTagList() {
    if (this.creatingTagList || !this.campaign || !this.campaign._id) {
      return;
    }
    const store = this.locationService.getActiveLocation();
    if (!store || !store._id) {
      AppComponent.showSnack('Pick a store in the header first; the tag list belongs to that store.', 'OK', 5000);
      this.headerService.requestLocationsMenu();
      return;
    }
    this.creatingTagList = true;
    try {
      const result: any = await this.ioService.post('/group/createCampaignTagList', {
        groupId: this.campaign._id,
        locationId: store._id,
      });
      if (result && result.purchaseId) {
        AppComponent.showSnack('Price Tag List ' + (result.key || '') + ' created with ' + result.count + ' items.', 'OK', 5000);
        this.router.navigate(['/transfers', 'price-tags', result.purchaseId]);
      } else {
        AppComponent.showSnack((result && result.msg) || 'Could not create the tag list.', 'OK', 5000);
      }
    } catch (e) {
      AppComponent.showSnack((e && e.msg) || (e && e.error && e.error.msg) || 'Could not create the tag list.', 'OK', 5000);
    }
    this.creatingTagList = false;
  }

  // One configured template prints immediately; several open the picker modal.
  printTags() {
    if (this.printTemplates.length === 1) {
      this.printTagsWithTemplate(this.printTemplates[0].key);
    } else {
      this.printTemplateSelect = true;
    }
  }

  cancelPrintTemplateSelect() {
    this.printTemplateSelect = false;
  }

  // Renders a mini page thumbnail for the picker from the template's
  // previewGrid metadata ([columns, rows] of talkers per sheet).
  previewCells(template: any): any[] {
    const grid = Array.isArray(template.previewGrid) ? template.previewGrid : [1, 1];
    const count = Math.max(1, (grid[0] || 1) * (grid[1] || 1));
    return new Array(count).fill(0);
  }

  previewColumns(template: any): string {
    const grid = Array.isArray(template.previewGrid) ? template.previewGrid : [1, 1];
    return 'repeat(' + Math.max(1, grid[0] || 1) + ', 1fr)';
  }

  async printTagsWithTemplate(templateKey: string) {
    this.printingTemplateKey = templateKey;
    try {
      await this.tagPrintService.print('report/loadRecords', {
        key: 'sale-campaign-tags',
        templateKey,
        utcOffset: new Date().getTimezoneOffset(),
        clientConfig: {
          download: true,
          clientFilters: [
            {
              path: 'groupId',
              type: 'id',
              value: this.campaign._id
            }
          ]
        }
      });
      this.printTemplateSelect = false;
    } catch (e) {
      // The server says why when it refuses (e.g. a viewer printing before the
      // campaign is active); otherwise the generic message.
      const msg = e?.error?.msg || e?.msg;
      AppComponent.showSnack(msg || 'Print failed — the tag report or template is unavailable.', 'OK', 4000);
    }
    this.printingTemplateKey = null;
  }

  close() {
    this.reportService.loadReport(null);
    this.reportService.closeDetailPanel(this.route);
  }

}
